import { v4 as uuidv4 } from 'uuid';

import { getDeepCopy } from 'utils/helpers';
import {
  FormBlock,
  FormField,
  FormFieldType,
  OutcomeFieldsFormField,
} from 'types';
import {
  fieldArrayDisallowedNestedFieldTypes,
  outcomeFieldsDisallowedNestedFieldTypes,
} from 'utils/form';

import {
  ClipboardEntityType,
  FieldCompatibilityRules,
  FormBuilderClipboardPayloadV1,
} from './types';

const CLIPBOARD_KIND = 'form-builder-clipboard';
const CLIPBOARD_VERSION = 1;
const CLIPBOARD_PREFIX = `${CLIPBOARD_KIND}:v${CLIPBOARD_VERSION}:`;

const fieldTypeValues = new Set<string>(Object.values(FormFieldType));

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const encodeBase64 = (value: string) => {
  const bytes = new TextEncoder().encode(value);
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join(
    ''
  );
  return btoa(binary);
};

const decodeBase64 = (value: string) => {
  const binary = atob(value);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const toOutcomeSourceKey = (stepId: string, fieldId: string) =>
  `${stepId}:${fieldId}`;

const getNestedFields = (field: FormField): FormField[] => {
  if (field.type === FormFieldType.FIELD_ARRAY) {
    return field.options?.fields ?? [];
  }

  if (field.type === FormFieldType.OUTCOME_FIELDS) {
    return field.options?.fields ?? [];
  }

  return [];
};

const removeCsvLookupSource = (field: FormField): FormField => {
  if (field.type === FormFieldType.CSV_LOOKUP) {
    const options = { ...field.options };
    delete options.datasetId;
    delete options.itemsFormId;
    delete options.sourceFile;

    return {
      ...field,
      options,
    };
  }

  if (field.type === FormFieldType.FIELD_ARRAY && field.options) {
    return {
      ...field,
      options: {
        ...field.options,
        fields: field.options.fields.map(removeCsvLookupSource),
      },
    };
  }

  if (field.type === FormFieldType.OUTCOME_FIELDS && field.options) {
    return {
      ...field,
      options: {
        ...field.options,
        fields: field.options.fields.map(removeCsvLookupSource),
      },
    };
  }

  return field;
};

const removeCsvLookupSources = (
  data: FormField | FormBlock
): FormField | FormBlock =>
  'fields' in data
    ? {
        ...data,
        fields: data.fields.map(removeCsvLookupSource),
      }
    : removeCsvLookupSource(data);

const sanitizeOutcomeFieldSource = (
  field: OutcomeFieldsFormField,
  availableSourceKeys: Set<string>
): OutcomeFieldsFormField => {
  if (!field.options) {
    return field;
  }

  const sourceCourseStepId = field.options?.sourceCourseStepId ?? '';
  const sourceFieldId = field.options?.sourceFieldId ?? '';

  if (!sourceCourseStepId || !sourceFieldId) {
    return field;
  }

  if (
    availableSourceKeys.has(
      toOutcomeSourceKey(sourceCourseStepId, sourceFieldId)
    )
  ) {
    return field;
  }

  console.warn(
    '[FormBuilder] Outcome field source reference cleared on paste: step "%s", field "%s" not found in available sources',
    sourceCourseStepId,
    sourceFieldId
  );

  return {
    ...field,
    options: {
      ...field.options,
      sourceFieldId: '',
      sourceCourseStepId: '',
      fields: field.options.fields ?? [],
    },
  };
};

const FIELD_ARRAY_CHILD_RULES: FieldCompatibilityRules = {
  allowOutcomes: !fieldArrayDisallowedNestedFieldTypes.has(
    FormFieldType.OUTCOMES
  ),
  allowPageBreak: !fieldArrayDisallowedNestedFieldTypes.has(
    FormFieldType.PAGE_BREAK
  ),
  allowFieldArray: !fieldArrayDisallowedNestedFieldTypes.has(
    FormFieldType.FIELD_ARRAY
  ),
  allowOutcomeFields: !fieldArrayDisallowedNestedFieldTypes.has(
    FormFieldType.OUTCOME_FIELDS
  ),
};

const OUTCOME_FIELDS_CHILD_RULES: FieldCompatibilityRules = {
  allowOutcomes: !outcomeFieldsDisallowedNestedFieldTypes.has(
    FormFieldType.OUTCOMES
  ),
  allowPageBreak: !outcomeFieldsDisallowedNestedFieldTypes.has(
    FormFieldType.PAGE_BREAK
  ),
  allowFieldArray: !outcomeFieldsDisallowedNestedFieldTypes.has(
    FormFieldType.FIELD_ARRAY
  ),
  allowOutcomeFields: !outcomeFieldsDisallowedNestedFieldTypes.has(
    FormFieldType.OUTCOME_FIELDS
  ),
};

const regenerateFieldIds = (
  field: FormField,
  availableSourceKeys: Set<string>
): FormField => {
  let nextField: FormField = {
    ...field,
    id: uuidv4(),
  };
  delete nextField.referenceCode;

  if (nextField.type === FormFieldType.FIELD_ARRAY && nextField.options) {
    nextField = {
      ...nextField,
      options: {
        ...nextField.options,
        fields: nextField.options.fields.map((nestedField) =>
          regenerateFieldIds(nestedField, availableSourceKeys)
        ),
      },
    };
  }

  if (nextField.type === FormFieldType.OUTCOME_FIELDS && nextField.options) {
    nextField = {
      ...nextField,
      options: {
        ...nextField.options,
        fields: nextField.options.fields.map((nestedField) =>
          regenerateFieldIds(nestedField, availableSourceKeys)
        ),
      },
    };
    nextField = sanitizeOutcomeFieldSource(nextField, availableSourceKeys);
  }

  return nextField;
};

const getChildCompatibilityRules = (
  parentType: FormFieldType
): FieldCompatibilityRules | null => {
  if (parentType === FormFieldType.FIELD_ARRAY) {
    return FIELD_ARRAY_CHILD_RULES;
  }

  if (parentType === FormFieldType.OUTCOME_FIELDS) {
    return OUTCOME_FIELDS_CHILD_RULES;
  }

  return null;
};

const isFieldTypeAllowed = (
  type: FormFieldType,
  rules: FieldCompatibilityRules
) => {
  if (type === FormFieldType.OUTCOMES) return rules.allowOutcomes;
  if (type === FormFieldType.OUTCOME_FIELDS) return rules.allowOutcomeFields;
  if (type === FormFieldType.FIELD_ARRAY) return rules.allowFieldArray;
  if (type === FormFieldType.PAGE_BREAK) return rules.allowPageBreak;
  return true;
};

const validateFieldSchema = (field: unknown): string | null => {
  if (!isObject(field)) return 'Field is not an object';
  if (!fieldTypeValues.has(String(field.type))) return 'Field type is invalid';

  const fieldType = field.type as FormFieldType;
  if (
    fieldType === FormFieldType.FIELD_ARRAY ||
    fieldType === FormFieldType.OUTCOME_FIELDS
  ) {
    const options = field.options;
    if (options !== undefined) {
      if (!isObject(options)) return 'Field options is not an object';
      const nestedFields = (options as Record<string, unknown>).fields;
      if (nestedFields !== undefined) {
        if (!Array.isArray(nestedFields))
          return 'Nested fields is not an array';
        for (const nestedField of nestedFields) {
          const error = validateFieldSchema(nestedField);
          if (error) return error;
        }
      }
    }
  }

  return null;
};

const getClipboardPayloadSchemaError = (value: unknown): string | null => {
  if (!isObject(value)) {
    return 'Clipboard payload is not an object';
  }

  if (value.kind !== CLIPBOARD_KIND) {
    return 'Clipboard payload kind is not supported';
  }

  if (value.version !== CLIPBOARD_VERSION) {
    return 'Clipboard payload version is not supported';
  }

  if (value.entityType !== 'field' && value.entityType !== 'section') {
    return 'Clipboard payload entity type is not supported';
  }

  if (!isObject(value.data)) {
    return 'Clipboard payload does not contain data';
  }

  if (value.metadata !== undefined) {
    if (!isObject(value.metadata)) {
      return 'Clipboard metadata is invalid';
    }

    if (
      value.metadata.sectionExpanded !== undefined &&
      typeof value.metadata.sectionExpanded !== 'boolean'
    ) {
      return 'Clipboard sectionExpanded metadata is invalid';
    }
  }

  if (value.entityType === 'field') {
    const fieldError = validateFieldSchema(value.data);
    if (fieldError) return fieldError;
  } else {
    if (!Array.isArray(value.data.fields)) {
      return 'Clipboard section fields are invalid';
    }
    for (const field of value.data.fields) {
      const fieldError = validateFieldSchema(field);
      if (fieldError) return fieldError;
    }
  }

  return null;
};

export const buildClipboardPayload = (
  entityType: ClipboardEntityType,
  data: FormField | FormBlock,
  metadata?: FormBuilderClipboardPayloadV1['metadata']
): FormBuilderClipboardPayloadV1 => ({
  metadata,
  entityType,
  kind: CLIPBOARD_KIND,
  version: CLIPBOARD_VERSION,
  data: getDeepCopy(removeCsvLookupSources(data)),
});

export const writeBuilderClipboard = async (
  payload: FormBuilderClipboardPayloadV1
) => {
  const jsonPayload = JSON.stringify(payload);
  const encodedPayload = encodeBase64(jsonPayload);
  await navigator.clipboard.writeText(`${CLIPBOARD_PREFIX}${encodedPayload}`);
};

export const readBuilderClipboard =
  async (): Promise<FormBuilderClipboardPayloadV1> => {
    const clipboardText = await navigator.clipboard.readText();
    if (!clipboardText.trim()) {
      throw new Error('Clipboard is empty');
    }

    const isEncodedFormat = clipboardText.startsWith(CLIPBOARD_PREFIX);
    let payloadText = clipboardText;
    if (isEncodedFormat) {
      try {
        payloadText = decodeBase64(
          clipboardText.slice(CLIPBOARD_PREFIX.length)
        );
      } catch {
        throw new Error('Clipboard encoded payload is invalid');
      }
    }

    let parsedPayload: unknown;
    try {
      parsedPayload = JSON.parse(payloadText);
    } catch {
      throw new Error('Clipboard does not contain valid JSON');
    }

    const schemaError = getClipboardPayloadSchemaError(parsedPayload);
    if (schemaError) {
      throw new Error(schemaError);
    }

    return parsedPayload as FormBuilderClipboardPayloadV1;
  };

export const cloneFieldForPaste = (
  field: FormField,
  availableSourceKeys: Set<string>
) => regenerateFieldIds(getDeepCopy(field), availableSourceKeys);

export const cloneSectionForPaste = (
  section: FormBlock,
  availableSourceKeys: Set<string>
): FormBlock => {
  const copiedSection = getDeepCopy(section);

  return {
    ...copiedSection,
    id: uuidv4(),
    fields: copiedSection.fields.map((field) =>
      regenerateFieldIds(field, availableSourceKeys)
    ),
  };
};

export const findFirstIncompatibleFieldType = (
  field: FormField,
  rules: FieldCompatibilityRules
): FormFieldType | null => {
  if (!isFieldTypeAllowed(field.type, rules)) {
    return field.type;
  }

  const childRules = getChildCompatibilityRules(field.type);
  if (!childRules) {
    return null;
  }

  for (const childField of getNestedFields(field)) {
    const invalidType = findFirstIncompatibleFieldType(childField, childRules);
    if (invalidType) {
      return invalidType;
    }
  }

  return null;
};

export const createOutcomeSourceKeys = (
  sources: Array<{ stepId: string; fieldId: string }>
) =>
  new Set(
    sources.map((source) => toOutcomeSourceKey(source.stepId, source.fieldId))
  );
